import type { RoleEnum, User, UserPermissions, UserTierEnum, Workspace } from 'js/src/client';
import { ApiService, Urls } from 'js/src/client';
import { Toast } from 'js/src/components/common/Toast';
import {
    setInvitationsLoading,
    setProjectLoadingIndicator,
    storeInvitations,
    storeProjects,
    storeTeamMembers,
    storeTeamSettings,
    storeUser,
    storeWorkspaceMembers,
    storeWorkspaces,
    updateTeamMember,
    updateWorkspace,
} from 'js/src/data/account/account-actions';
import {
    getAllTeamMembers,
    getUserPermissions,
    selectWorkspaceByOrmId,
} from 'js/src/data/account/account-selectors';
import type { SocialCompletePayload } from 'js/src/data/account/account-types';
import { showModal } from 'js/src/data/modals/modals-actions';
import { LOCKED_FEATURES, MODAL_IDS } from 'js/src/data/modals/modals-types';
import type { ImmutableMap } from 'js/src/libs/consts';
import { ImmutableFromJS, ImmutableToJS } from 'js/src/libs/consts';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import type { AppDispatch } from 'js/src/libs/RootState';
import type { GetRootState } from 'js/src/luminary/luminary-redux-connect-types';
import { isApiErrorWithProperty } from 'js/src/types';
import Cookies from 'js-cookie';

function _storeUserResponse(dispatch: AppDispatch, response: User): void {
    dispatch(storeUser(response));
}

export function updateUser(response: User) {
    return (dispatch: AppDispatch) => {
        _storeUserResponse(dispatch, response);
    };
}

export function loadWorkspaces() {
    return (dispatch: AppDispatch) => {
        ApiService.workspaceList()
            .then((response) => {
                dispatch(
                    storeWorkspaces({
                        workspaces: ImmutableFromJS(response.data),
                    }),
                );
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading workspaces. Please try reloading page.');
            });
    };
}

export function removeMemberFromWorkspace(member_id: number, workspace_id: number) {
    return (dispatch: AppDispatch) => {
        return new Promise<void>((resolve, reject) => {
            ApiService.workspaceRemoveMemberDestroy({
                path: { id: workspace_id },
                query: { user_id: member_id },
            })
                .then(() => {
                    dispatch(loadTeamMembers()); // reload team members to update the project member state
                    analytics.track('Team Member Removed From Workspace');
                    resolve();
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    let error_message =
                        'Error removing team member from workspace - please try again';
                    if (isApiErrorWithProperty(error, 'error') && error.body.error) {
                        error_message = error.body.error as string;
                    }
                    Toast.error(error_message);
                    reject(new Error(error_message));
                });
        });
    };
}

export function addMembersToWorkspace(
    user_ids: number[],
    workspace_id: number,
    updateStateBeforeApiCall: boolean = false,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        if (updateStateBeforeApiCall) {
            // Optimistically update the workspace members
            const members: Array<User> = ImmutableToJS(getAllTeamMembers(getState()));
            dispatch(storeWorkspaceMembers({ workspaceId: workspace_id, members }));
        }
        ApiService.workspaceAddMembersCreate({
            path: { id: workspace_id },
            body: {
                user_ids,
            },
        })
            .then(() => {
                dispatch(loadTeamMembers()); // reload team members to update the project member state
                analytics.track('Team Member Added To Workspace');
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error adding team member to workspace. Please try again.');
            });
    };
}

export function workspacePartialUpdate(
    settings: Partial<Workspace>,
    workspaceOrmId: number,
    preUpdate = true,
    successCallback: (() => void) | null = null,
    errorCallback: ((response: unknown) => void) | null = null,
) {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        // preemptively update the workspace to keep it feeling snappy
        const preWorkspace = selectWorkspaceByOrmId(getState(), workspaceOrmId);
        if (!preWorkspace) throw new Error('Workspace not found');
        const workspaceId = preWorkspace.get('id');
        if (preUpdate) {
            const updatedWorkspace = preWorkspace.merge(
                settings,
            ) as unknown as ImmutableMap<Workspace>;
            dispatch(updateWorkspace({ workspace: updatedWorkspace, workspaceId }));
        }
        ApiService.workspacePartialUpdate({
            path: { id: workspaceId },
            body: settings,
        })
            .then((response) => {
                dispatch(
                    updateWorkspace({
                        workspace: ImmutableFromJS(response.data),
                        workspaceId,
                    }),
                );
                if (successCallback) {
                    successCallback();
                }
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                if (errorCallback) {
                    errorCallback(error);
                } else {
                    Toast.error('There was an error applying the change, please try again.');
                    if (preUpdate) {
                        dispatch(
                            updateWorkspace({
                                workspace: preWorkspace,
                                workspaceId,
                            }),
                        );
                    }
                }
            });
    };
}

export function deleteWorkspace(workspace_id: number) {
    return (dispatch: AppDispatch) => {
        return new Promise<void>((resolve, reject) => {
            ApiService.workspaceDestroy({
                path: { id: workspace_id },
            })
                .then(() => {
                    dispatch(loadWorkspaces());
                    analytics.track('Workspace Deleted');
                    resolve();
                })
                .catch((error: unknown) => {
                    ErrorReporter.capture(error);
                    Toast.error('Error deleting workspace. Please try again.');
                    reject(new Error('error deleting workspace'));
                });
        });
    };
}

export function createWorkspace(name: string) {
    return (dispatch: AppDispatch) => {
        return ApiService.workspaceCreate({
            body: {
                name,
            },
        })
            .then(() => {
                dispatch(loadWorkspaces());
                analytics.track('Workspace Created');
            })
            .catch((error: unknown) => {
                if (
                    isApiErrorWithProperty(error, 'error') &&
                    error.body?.error === 'workspace_limit_reached'
                ) {
                    analytics.track('Tried Creating New Workspace But At Limit');
                    dispatch(
                        showModal({
                            modal_id: MODAL_IDS.FEATURE_LOCK,
                            modal_context: ImmutableFromJS({
                                featureName: LOCKED_FEATURES.HIT_MAX_WORKSPACES,
                            }),
                        }),
                    );
                } else {
                    // handle unknown failure
                    ErrorReporter.capture(error);
                    analytics.track('Error creating workspace');
                    Toast.error('Error creating workspace. Please try again.');
                }
            });
    };
}

export function loadTeamMembers() {
    return (dispatch: AppDispatch) => {
        ApiService.teamMembersList()
            .then((response) => {
                dispatch(storeTeamMembers({ members: response.data }));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading team members. Please try again.');
            });
    };
}

export function changeTeamMemberRole(userId: number, new_role: RoleEnum) {
    return (dispatch: AppDispatch) => {
        ApiService.teamMembersPartialUpdate({
            path: { id: userId },
            body: {
                role: new_role,
            },
        })
            .then((response) => {
                analytics.track('Team Member role changed', {
                    role: response.data.role,
                });
                dispatch(
                    updateTeamMember({
                        userId,
                        key: ['role'],
                        value: response.data.role,
                    }),
                );
                Toast.success('User role changed');
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                analytics.track('Error changing team role');
                Toast.error('Error changing team role - please try again');
            });
    };
}

export function changeTeamMemberTier(userId: number, new_tier: UserTierEnum) {
    return (dispatch: AppDispatch) => {
        ApiService.teamMembersPartialUpdate({
            path: { id: userId },
            body: {
                user_tier: new_tier,
            },
        })
            .then((response) => {
                analytics.track('Team Member tier changed', {
                    user_tier: response.data.user_tier,
                });
                dispatch(
                    updateTeamMember({
                        userId,
                        key: ['user_tier'],
                        value: response.data.user_tier,
                    }),
                );
                Toast.success('User setting changed');
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                analytics.track('Error changing user tier');
                Toast.error('Error changing user tier - please try again');
            });
    };
}

export function loadUser() {
    return (dispatch: AppDispatch) => {
        ApiService.userRetrieve()
            .then((response) => {
                _storeUserResponse(dispatch, response.data);
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error(
                    'Error loading user information. Please refresh the page and try again.',
                );
            });
    };
}

export function loadProjects() {
    return (dispatch: AppDispatch, getState: GetRootState) => {
        dispatch(setProjectLoadingIndicator({ isLoading: true }));
        ApiService.projectsList()
            .then((response) => {
                dispatch(storeProjects({ projects: response.data }));
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading projects. Please try again.');
            });
    };
}

export function loadTeamSettings() {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await ApiService.teamSettingsRetrieve();
            dispatch(storeTeamSettings(response.data));
        } catch (error) {
            ErrorReporter.capture(error);
            Toast.error('Problem loading team information. Please refresh the page and try again.');
        }
    };
}

export const continueSocialComplete = (
    requestPayload: SocialCompletePayload,
    backend: string,
    getPayload?: Record<string, string>,
) => {
    let url = Urls.SOCIAL_AUTH_COMPLETE.replace('backend', backend);
    if (getPayload && Object.values(getPayload).length > 0) {
        url += '?' + new URLSearchParams(getPayload).toString();
    }

    return $.ajax({
        url,
        method: 'POST',
        data: requestPayload,
        beforeSend: function (request) {
            request.setRequestHeader('X-CSRF-Token', Cookies.get('csrftoken') ?? '');
        },
    });
};

export function loadInvitations() {
    return (dispatch: AppDispatch) => {
        dispatch(setInvitationsLoading({ isLoading: true }));
        ApiService.teamInvitationsList()
            .then((response) => {
                dispatch(
                    storeInvitations({
                        invitations: ImmutableFromJS(response.data),
                    }),
                );
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                Toast.error('Error loading pending invitations. Please try again.');
            })
            .finally(() => {
                dispatch(setInvitationsLoading({ isLoading: false }));
            });
    };
}

export const loginSignupOperation = ({
    name,
    email,
    password,
    is_signup,
    invite_uuid,
}: {
    name?: string;
    email: string;
    password: string;
    is_signup: string;
    invite_uuid?: string;
}) => {
    return $.ajax({
        url: Urls.EMAIL_AUTH_COMPLETE + window.location.search,
        method: 'POST',
        data:
            is_signup === '1'
                ? // signup fields
                  {
                      name,
                      email,
                      password,
                      is_signup,
                      invite_uuid,
                  }
                : // login fields
                  {
                      email,
                      password,
                      is_signup,
                      invite_uuid,
                  },
        beforeSend: function (request) {
            request.setRequestHeader('X-CSRFToken', Cookies.get('csrftoken') ?? '');
        },
    });
};

/**
 * Update user permissions.
 *
 * @param userId - an integer that indicates the user id
 * @param changedPermissions - An array of changed permissions
 * @param shouldAddPermission - a boolean that indicates whether the changed permissions should be added or removed from the user's existing permissions
 */
export function updateUserPermissions(
    userId: number,
    changedPermissions: UserPermissions[],
    shouldAddPermission: boolean,
) {
    return async (dispatch: AppDispatch, getState: GetRootState) => {
        const originalUserPermissions = getUserPermissions(getState(), userId);
        let updatedUserPermissions = originalUserPermissions;
        if (shouldAddPermission) {
            updatedUserPermissions = originalUserPermissions.concat(
                changedPermissions.filter(
                    (permission) => originalUserPermissions.indexOf(permission) < 0,
                ),
            );
        } else {
            updatedUserPermissions = originalUserPermissions.filter((userPermission) => {
                return !changedPermissions.includes(userPermission);
            });
        }

        dispatch(
            updateTeamMember({
                userId,
                key: ['permissions'],
                value: updatedUserPermissions,
            }),
        );
        try {
            await ApiService.teamMembersPartialUpdate({
                path: { id: userId },
                body: {
                    permissions: updatedUserPermissions,
                },
            });
            // In the UI, the user can click a lot of different checkboxes really fast.
            // To avoid a Toast pile-up, we remove all existing notifications before showing a success message.
            Toast.removeAll();
            Toast.success('Permissions updated!');
        } catch (error: unknown) {
            ErrorReporter.capture(error);
            dispatch(
                updateTeamMember({
                    userId,
                    key: ['permissions'],
                    value: originalUserPermissions,
                }),
            );
            Toast.error('Error updating permissions - please try again');
        }
    };
}

export function removeUserFromTeam(userId: number) {
    return (dispatch: AppDispatch) => {
        ApiService.teamMembersDestroy({
            path: { id: userId },
        })
            .then(() => {
                analytics.track('Team Member Deleted');
                Toast.success('Team member has been removed!');
                dispatch(loadTeamMembers());
                dispatch(loadTeamSettings());
            })
            .catch((error: unknown) => {
                ErrorReporter.capture(error);
                analytics.track('Error Deleting Team Member');
                Toast.error('Error deleting team member - please try again');
            });
    };
}
